import { defineComponent as _defineComponent } from 'vue'
import { storeToRefs } from 'pinia';
    import { useTHGCarStore } from '@/storenew/thg/cars';
    import { computed, ref } from 'vue';
    import {
        alertActionError,
        alertInfo,
        confirm,
        datePrompt,
        numberPrompt,
    } from '@/utils/dialogs';
    import { processActionResponse } from '@/utils/thg';
    import emitter from '@/utils/mitt';
    import {
        triggerInfoMailSending,
        triggerOverrideCO2Equivalent,
        triggerOverrideStatus,
        triggerPayout,
        triggerAddTags,
        triggerRenew,
        triggerStatusUpdate,
    } from '@/api/thg/actions/cars';
    import { THGCarUpdateStatusPayload } from '@/interfaces/thg/THGCarUpdateStatusPayload';
    import { THGInfoMailPayload } from '@/interfaces/thg/THGInfoMailPayload';
    import {
        THGCarAddTagsPayload,
        THGCarOverrideCO2EquivalentPayload,
        THGCarOverrideStatusPayload,
        THGCarPayoutActionResponse,
        THGCarPayoutPayload,
        THGCarRenewPayload,
    } from '@/interfaces/thg/THGCarActions';
    import { ModalProgrammatic } from 'buefy';
    import THGTagSelectModal from '@/components/thg/THGTagSelectModal.vue';
    import { THGTag } from '@/interfaces/thg/THGTag';
    import { useTHGGlobalStore } from '@/storenew/thg/global';
    import { Country } from '@/enums/Country';
    import { displayPrice } from '@/utils/misc';
    import { THGActionType } from '@/enums/thg/THGActionType';
    import { THGActionResponse } from '@/interfaces/thg/THGActionResponse';
    import { THGCarStatus } from '@/enums/thg/THGCarStatus';

    
export default /*#__PURE__*/_defineComponent({
  __name: 'THGCarActions',
  setup(__props) {

    const store = useTHGCarStore();
    const { filterParam } = storeToRefs(store);
    const { currentCountry } = storeToRefs(useTHGGlobalStore());

    const chosenAction = ref<THGActionType | null>(null);
    const newCO2Equiv = ref<number | undefined>(undefined);
    const newUBASubmittedDate = ref<string | undefined>(undefined);
    const renewalAmount = ref<number | undefined>(undefined);
    const selectedTags = ref<THGTag[]>([]);

    const enableActionButton = computed<boolean>(() => {
        // enable action button only if action itself and a year is selected
        return Boolean(chosenAction.value && filterParam.value.year);
    });

    /**
     * Trigger the action via api by actionType.
     *
     * @param actionType
     * @param pretend
     */
    async function triggerAction(
        actionType: THGActionType,
        pretend = true,
    ): Promise<THGActionResponse | THGCarPayoutActionResponse> {
        if (!chosenAction.value) {
            throw new Error('no action selected');
        }

        if (
            actionType === THGActionType.REGISTRATION_DECLINE ||
            actionType === THGActionType.SUBMISSION ||
            actionType === THGActionType.RECEIVING ||
            actionType === THGActionType.DECLINE
        ) {
            const payload: THGCarUpdateStatusPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                action_type: chosenAction.value,
            };

            // trigger action via api
            const resp = await triggerStatusUpdate(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.INFOMAIL) {
            const payload: THGInfoMailPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                action_type: chosenAction.value,
                mail_body: '',
                mail_tag: '2025-payoff-iban-reminder',
            };

            // trigger action via api
            const resp = await triggerInfoMailSending(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.PAYOUT) {
            const payload: THGCarPayoutPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                action_type: chosenAction.value,
            };

            // trigger action via api
            const resp = await triggerPayout(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.OVERRIDE_CO2_EQUIVALENT) {
            if (!newCO2Equiv.value) {
                throw new Error('New CO₂ is undefined.');
            }

            const payload: THGCarOverrideCO2EquivalentPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                new_co2_equivalent: newCO2Equiv.value,
            };

            const resp = await triggerOverrideCO2Equivalent(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.OVERRIDE_STATUS_DATA_RECEIVED) {
            const payload: THGCarOverrideStatusPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                new_status: THGCarStatus.DATA_RECEIVED,
            };

            const resp = await triggerOverrideStatus(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.OVERRIDE_STATUS_UBA_SUBMITTED) {
            const payload: THGCarOverrideStatusPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                new_status: THGCarStatus.UBA_SUBMITTED,
                uba_submitted_date: newUBASubmittedDate.value,
            };

            const resp = await triggerOverrideStatus(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.OVERRIDE_STATUS_CERT_DECLINED) {
            const payload: THGCarOverrideStatusPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                new_status: THGCarStatus.CERT_DECLINED,
                uba_submitted_date: newUBASubmittedDate.value,
            };

            const resp = await triggerOverrideStatus(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.OVERRIDE_STATUS_CERT_RECEIVED) {
            const payload: THGCarOverrideStatusPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                new_status: THGCarStatus.CERT_RECEIVED,
                uba_submitted_date: newUBASubmittedDate.value,
            };

            const resp = await triggerOverrideStatus(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.OVERRIDE_STATUS_INCOME_PAID_OUT) {
            const payload: THGCarOverrideStatusPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                new_status: THGCarStatus.INCOME_PAID_OUT,
                uba_submitted_date: newUBASubmittedDate.value,
            };

            const resp = await triggerOverrideStatus(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.RENEW) {
            if (!renewalAmount.value) {
                throw new Error('Renewal amount is undefined.');
            }

            const payload: THGCarRenewPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                action_type: THGActionType.RENEW,
                new_base_payoff_amount: renewalAmount.value,
            };

            const resp = await triggerRenew(payload);

            return processActionResponse(resp);
        } else if (actionType === THGActionType.BULK_ADD_TAGS) {
            if (selectedTags.value.length === 0) {
                throw new Error('No tags selected.');
            }

            const payload: THGCarAddTagsPayload = {
                pretend: pretend,
                filter_params: filterParam.value,
                action_type: THGActionType.BULK_ADD_TAGS,
                tags: selectedTags.value.map((tag) => tag._id!),
            };

            const resp = await triggerAddTags(payload);

            return processActionResponse(resp);
        }
        throw new Error('unknown action type chosen.');
    }

    /**
     * Trigger the action
     */
    async function apply() {
        const initialQuestion = `Soll die ausgewählte Aktion <strong>${(
            chosenAction.value as string
        ).toUpperCase()}</strong> wirklich gestartet werden?`;
        const { result: initialResult, dialog: initialDialog } = await confirm(
            'Aktion starten',
            initialQuestion,
            'Starten',
            'is-primary',
        );

        if (initialResult && chosenAction.value) {
            if (chosenAction.value === THGActionType.OVERRIDE_CO2_EQUIVALENT) {
                const { result: co2EquivResult, dialog: co2EquivDialog } = await numberPrompt(
                    'Bitte das neue CO₂-Äquivalent eingeben.',
                    'CO₂-Äquivalent',
                    0,
                    100,
                    0.001,
                );

                if (!co2EquivDialog || !co2EquivResult) {
                    // stop when 'Cancel' button was used
                    initialDialog.close();
                    return;
                }

                newCO2Equiv.value = parseFloat(co2EquivResult.toString());
            }

            if (chosenAction.value === THGActionType.RENEW) {
                const { result: amountResult, dialog: amountDialog } = await numberPrompt(
                    'Bitte den Betrag eingeben.',
                    'Betrag',
                    0,
                    10000,
                    0.01,
                );

                if (!amountDialog || !amountResult) {
                    initialDialog.close();
                    return;
                }

                renewalAmount.value = parseFloat(amountResult.toString());
            }

            if (chosenAction.value === THGActionType.BULK_ADD_TAGS) {
                const result = await new Promise<THGTag[] | null>((resolve) => {
                    const modal = ModalProgrammatic.open({
                        component: THGTagSelectModal,
                        trapFocus: true,
                        hasModalCard: true,
                        events: {
                            select: (tags: THGTag[]) => {
                                resolve(tags);
                                modal.close();
                            },
                        },
                        onCancel: () => resolve(null),
                    });
                });

                if (!result || result.length === 0) {
                    initialDialog.close();
                    return;
                }

                selectedTags.value = result;
            }

            if (
                [
                    THGActionType.OVERRIDE_STATUS_UBA_SUBMITTED,
                    THGActionType.OVERRIDE_STATUS_CERT_DECLINED,
                    THGActionType.OVERRIDE_STATUS_CERT_RECEIVED,
                    THGActionType.OVERRIDE_STATUS_INCOME_PAID_OUT,
                ].includes(chosenAction.value)
            ) {
                const { result: ubaSubmittedDateResult, dialog: ubaSubmittedDateDialog } =
                    await datePrompt(
                        'Bitte das Datum der Einreichung eingeben. Lasse es leer, wenn es nicht überschrieben werden soll.',
                    );

                if (!ubaSubmittedDateDialog || !ubaSubmittedDateResult) {
                    // stop when 'Cancel' button was used
                    initialDialog.close();
                    return;
                }

                if (typeof ubaSubmittedDateResult === 'string') {
                    // in case of 'empty' -> it's a boolean
                    //newUBASubmittedDate.value = parseISO(ubaSubmittedDateResult);
                    newUBASubmittedDate.value = ubaSubmittedDateResult;
                }
            }

            // start loading
            emitter.emit('showLoader');

            // make pretend action
            let pretendResponse: THGActionResponse | THGCarPayoutActionResponse;

            try {
                pretendResponse = await triggerAction(chosenAction.value);
            } catch (e) {
                alertActionError(e, initialDialog);
                return;
            }

            // hide the loader and close
            emitter.emit('hideLoader');
            initialDialog.close();

            let pricePerTonHint = '';

            if (chosenAction.value === THGActionType.PAYOUT) {
                if (currentCountry.value === Country.DE) {
                    const pricePerTon = (pretendResponse as THGCarPayoutActionResponse)
                        .price_per_ton;
                    pricePerTonHint = `Der aktuelle Preis pro Tonne beträgt <strong>${displayPrice(
                        pricePerTon,
                    )}</strong>.<br>`;
                }
            }

            const finalQuestion = `${pricePerTonHint}Die ausgewählte Aktion betrifft
                                            ${pretendResponse.cars_count} Fahrzeuge. Bitte vergleiche diese Anzahl mit der hier in der
                                            App angezeigten! Wirklich ausführen?`;

            const { result: finalResult, dialog: finalDialog } = await confirm(
                'Ausführung bestätigen',
                finalQuestion,
                'Ja, ausführen',
                'is-warning',
            );

            if (finalResult) {
                // start loading
                emitter.emit('showLoader');

                // trigger the 'real' action
                let finalResponse: THGActionResponse;

                try {
                    finalResponse = await triggerAction(chosenAction.value, false);
                } catch (e) {
                    alertActionError(e, finalDialog);
                    return;
                }

                // hide the loader and close
                emitter.emit('hideLoader');
                finalDialog.close();

                alertInfo(
                    `Aktion ${(chosenAction.value as string).toUpperCase()} ausgeführt`,
                    `Betroffene Fahrzeuge: ${finalResponse.cars_count}`,
                );
            }
        }
    }

return { __sfc: true,store, filterParam, currentCountry, chosenAction, newCO2Equiv, newUBASubmittedDate, renewalAmount, selectedTags, enableActionButton, triggerAction, apply, Country, THGActionType }
}

})